import { __extends } from "tslib";
import { AssetEvent, Box, Matrix3D, Rectangle, } from '@awayjs/core';
import { AttributesBuffer, BlendMode, Image2D, ImageSampler, ContextGLTriangleFace } from '@awayjs/stage';
import { ContainerNodeEvent } from '@awayjs/view';
import { Style } from './base/Style';
import { _Render_RenderableBase } from './base/_Render_RenderableBase';
import { _Render_RendererMaterial } from './base/_Render_RendererMaterial';
import { TriangleElements } from './elements/TriangleElements';
import { MaterialEvent } from './events/MaterialEvent';
import { RenderableEvent } from './events/RenderableEvent';
import { StyleEvent } from './events/StyleEvent';
import { RendererBase } from './RendererBase';
import { ImageTexture2D } from './textures/ImageTexture2D';
import { Settings as StageSettings } from '@awayjs/stage';
import { Settings } from './Settings';
var CacheRenderer = /** @class */ (function (_super) {
    __extends(CacheRenderer, _super);
    function CacheRenderer(partition, pool) {
        var _this = _super.call(this, partition, pool) || this;
        _this._invalidDimension = false;
        _this._renderMatrix = new Matrix3D();
        _this._bounds = new Box();
        _this._paddedBounds = new Rectangle();
        /*internal*/ _this._boundsScale = 1;
        _this._textures = new Array();
        _this.animateUVs = false;
        _this.bothSides = false;
        _this.curves = false;
        _this.imageRect = false;
        _this.useColorTransform = true;
        _this.alphaThreshold = 0;
        _this._lockBlendMode = false;
        _this.node = partition.rootNode;
        _this._onTextureInvalidate = function (_event) { return _this.invalidate(); };
        _this._onInvalidateProperties = function (_event) { return _this._invalidateStyle(); };
        _this._onInvalidateParentNode = function (_event) { return _this.onInvalidate(null); };
        _this._onInvalidateColorTransform = function (_event) {
            _this.onInvalidate(null);
        };
        if (partition.parent) {
            _this._parentNode = partition.parent.rootNode;
            _this._parentNode.addEventListener(ContainerNodeEvent.INVALIDATE_MATRIX3D, _this._onInvalidateParentNode);
            _this._parentNode.addEventListener(ContainerNodeEvent.INVALIDATE_COLOR_TRANSFORM, _this._onInvalidateColorTransform);
        }
        // for check filters/blends changes
        _this.node.container.addEventListener(RenderableEvent.INVALIDATE_STYLE, _this._onInvalidateParentNode);
        _this.node.container.addEventListener(ContainerNodeEvent.INVALIDATE_COLOR_TRANSFORM, _this._onInvalidateColorTransform);
        _this.style = new Style();
        _this.texture = new ImageTexture2D();
        _this._boundsPicker = _this._renderGroup.pickGroup.getBoundsPicker(_this._partition);
        _this._boundsDirty = true;
        _this._traverserClass = CacheRenderer;
        return _this;
    }
    Object.defineProperty(CacheRenderer.prototype, "assetType", {
        get: function () {
            return CacheRenderer.assetType;
        },
        enumerable: true,
        configurable: true
    });
    CacheRenderer.prototype.getPaddedBounds = function () {
        if (this._boundsDirty)
            this._updateBounds();
        return this._paddedBounds;
    };
    CacheRenderer.prototype.getBounds = function () {
        if (this._boundsDirty)
            this._updateBounds();
        return this._bounds;
    };
    Object.defineProperty(CacheRenderer.prototype, "blendMode", {
        get: function () {
            if (this._lockBlendMode) {
                return BlendMode.LAYER;
            }
            var containerBlend = this.node.container.blendMode;
            // native blends
            switch (containerBlend) {
                case BlendMode.LAYER:
                case BlendMode.MULTIPLY:
                case BlendMode.NORMAL:
                case BlendMode.ADD:
                case BlendMode.SCREEN:
                case BlendMode.ALPHA:
                case BlendMode.SUBTRACT:
                    return containerBlend;
            }
            return BlendMode.LAYER;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CacheRenderer.prototype, "style", {
        /**
         *
         */
        get: function () {
            if (this._boundsDirty)
                this._updateBounds();
            return this._style;
        },
        set: function (value) {
            if (this._style == value)
                return;
            if (this._style)
                this._style.removeEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidateProperties);
            this._style = value;
            if (this._style)
                this._style.addEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidateProperties);
            this._invalidateStyle();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CacheRenderer.prototype, "texture", {
        /**
        * The 2d texture to use as a bitmap cache.
        */
        get: function () {
            //TODO check invalidation of _texture
            return this._texture;
        },
        set: function (value) {
            if (this._texture == value)
                return;
            if (this._texture)
                this.removeTexture(this._texture);
            this._texture = value;
            if (this._texture)
                this.addTexture(this._texture);
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    CacheRenderer.prototype.render = function (enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig) {
        var _this = this;
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        if (maskConfig === void 0) { maskConfig = 0; }
        var stage = this._stage;
        var sourceImage = this._style.image;
        if (!sourceImage) {
            return _super.prototype.render.call(this, enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig);
        }
        // WE MUST store older render target config,
        // because node can have cached child, that can be filtered
        this._stage.pushRenderTargetConfig();
        var targetImage;
        // we not require use TMP texture when not have MSAA
        if (stage.context.glVersion === 2 &&
            StageSettings.ENABLE_MULTISAMPLE_TEXTURE) {
            targetImage = stage.filterManager.popTemp(sourceImage.width, sourceImage.height, true);
        }
        // we should not render itself blended, disable it
        this._lockBlendMode = true;
        // we should render with colorTransform to self, enable it
        this.node.colorTransformDisabled = false;
        this._initRender(targetImage || sourceImage);
        _super.prototype.render.call(this, enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig);
        // restore colorTransform state as in transform state
        this.node.colorTransformDisabled = this.node.transformDisabled;
        // end enable blend
        this._lockBlendMode = false;
        if (sourceImage.width * sourceImage.height === 0) {
            debugger;
        }
        var container = this.node.container;
        //@ts-ignore
        var filters = container.filters;
        if (filters && filters.length > 0) {
            filters.forEach(function (e) { return e && (e.imageScale = _this._boundsScale); });
            stage.filterManager.applyFilters(targetImage || sourceImage, sourceImage, // because we use source as filter target - we not require copy
            sourceImage.rect, sourceImage.rect, filters);
        }
        else if (targetImage) {
            // this is fast, it should only call blitFramebuffer,
            // same as in regular MSAA
            stage.filterManager.copyPixels(targetImage, sourceImage, sourceImage.rect, sourceImage.rect.topLeft, false);
        }
        if (targetImage) {
            stage.filterManager.pushTemp(targetImage);
        }
        // pop render target after any filters,
        // required for deep filters (when node with filter has filtered child)
        this._stage.popRenderTarget();
    };
    // apply blend modes and swap texture if needed
    CacheRenderer.prototype.preActivateRenderPass = function () {
    };
    CacheRenderer.prototype.getNumTextures = function () {
        return this._textures.length;
    };
    CacheRenderer.prototype.getTextureAt = function (index) {
        return this._textures[index];
    };
    CacheRenderer.prototype.addTexture = function (texture) {
        this._textures.push(texture);
        texture.addEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidate);
        this.invalidate();
    };
    CacheRenderer.prototype.removeTexture = function (texture) {
        this._textures.splice(this._textures.indexOf(texture), 1);
        texture.removeEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidate);
        this.invalidate();
    };
    CacheRenderer.prototype.requestAbstraction = function (asset) {
        return _Render_Renderer;
    };
    /**
     * Marks the shader programs for all passes as invalid, so they will be recompiled before the next use.
     *
     * @private
     */
    CacheRenderer.prototype.invalidatePasses = function () {
        this.dispatchEvent(new MaterialEvent(MaterialEvent.INVALIDATE_PASSES, this));
    };
    /**
     *
     */
    CacheRenderer.prototype.enterNode = function (node) {
        var enter = _super.prototype.enterNode.call(this, node);
        if (enter && node.boundsVisible)
            this.applyEntity(node.getBoundsPrimitive(this._renderGroup.pickGroup));
        return enter;
    };
    CacheRenderer.prototype.onInvalidate = function (event) {
        _super.prototype.onInvalidate.call(this, event);
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_ELEMENTS, this));
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_STYLE, this));
        this.invalidate();
        this.invalidatePasses();
        this._boundsDirty = true;
    };
    CacheRenderer.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        this.texture.clear();
        this.texture = null;
        this.style.image.clear();
        this.style.image = null;
    };
    CacheRenderer.prototype._invalidateStyle = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_STYLE, this));
    };
    CacheRenderer.prototype._updateBounds = function () {
        this._boundsDirty = false;
        var matrix3D = this._renderMatrix;
        var container = this.node.container;
        var pad = this._paddedBounds;
        var view = this.rootView;
        //should be method to evaluate real scale relative screen
        var scale = view ? Math.min(3, view.projection.scale) : 1;
        this._boundsScale = scale;
        if (this._parentNode) {
            matrix3D.copyFrom(this._parentNode.getMatrix3D());
        }
        else {
            // no parent - no transform
            matrix3D.identity();
        }
        if (scale !== 1)
            matrix3D.appendScale(scale, scale, scale);
        var bounds = this._boundsPicker.getBoxBounds(this.node, true, true);
        if (!bounds) {
            console.error('[CachedRenderer] Bounds invalid, supress calculation', this.node);
            return;
        }
        if (isNaN(bounds.width) || isNaN(bounds.height)) {
            console.error('[CachedRenderer] Bounds invalid (NaN), supress calculation', this.node);
            return;
        }
        this._bounds.copyFrom(bounds);
        matrix3D.transformBox(this._bounds, this._bounds);
        matrix3D.invert();
        pad.setTo(this._bounds.x, this._bounds.y, this._bounds.width, this._bounds.height);
        if (container.filters && container.filters.length > 0) {
            container.filters.forEach(function (e) { return e && (e.imageScale = scale); });
            this._stage.filterManager.computeFiltersPadding(pad, container.filters, pad);
        }
        pad.x = (pad.x - 2) | 0;
        pad.y = (pad.y - 2) | 0;
        pad.width = (pad.width + 4) | 0;
        pad.height = (pad.height + 4) | 0;
        var image = this._style.image;
        if (pad.width * pad.height == 0) {
            debugger;
        }
        if (image) {
            this._style.image._setSize(pad.width, pad.height);
        }
        else {
            this._style.image = new Image2D(pad.width, pad.height, false);
            this._style.sampler = new ImageSampler(false, Settings.SMOOTH_CACHED_IMAGE, false);
            //this._view.target = this._style.image;
        }
    };
    CacheRenderer.prototype._initRender = function (target) {
        var pad = this._paddedBounds;
        var scale = this._boundsScale;
        var matrix3D = this._renderMatrix;
        var ox = pad.x - this._bounds.x;
        var oy = pad.y - this._bounds.y;
        var view = this._view;
        var proj = view.projection;
        matrix3D._rawData[14] = -1000;
        // without this we will handle empty image when target is big (scale > +3)
        proj.far = 4000;
        proj.near = 1;
        proj.transform.matrix3D = matrix3D;
        proj.ratio = (target.width / target.height);
        proj.originX = -1 - 2 * (pad.x - ox * 0.5) / target.width;
        proj.originY = -1 - 2 * (pad.y - oy * 0.5) / target.height;
        proj.scale = scale * 1000 / target.height;
        view.target = target;
    };
    CacheRenderer.registerMaterial = function (renderMaterialClass, materialClass) {
        CacheRenderer.materialClassPool[materialClass.assetType] = renderMaterialClass;
    };
    CacheRenderer.assetType = '[renderer CacheRenderer]';
    CacheRenderer.materialClassPool = {};
    CacheRenderer.renderGroupPool = {};
    CacheRenderer.defaultBackground = 0x0;
    return CacheRenderer;
}(RendererBase));
export { CacheRenderer };
var _Render_Renderer = /** @class */ (function (_super) {
    __extends(_Render_Renderer, _super);
    function _Render_Renderer() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    _Render_Renderer.prototype._getStageElements = function () {
        var asset = this._asset;
        var paddedBounds = asset.getPaddedBounds();
        var bounds = asset.getBounds();
        var offsetX = paddedBounds.x - bounds.x;
        var offsetY = paddedBounds.y - bounds.y;
        var matrix3D = Matrix3D.CALCULATION_MATRIX;
        matrix3D.copyFrom(this.renderSceneTransform);
        matrix3D.appendScale(asset._boundsScale, asset._boundsScale, asset._boundsScale);
        matrix3D.appendTranslation(offsetX * 0.5, offsetY * 0.5, 0);
        matrix3D.invert();
        var vectors = [
            paddedBounds.left, paddedBounds.top, 0,
            paddedBounds.right, paddedBounds.bottom, 0,
            paddedBounds.right, paddedBounds.top, 0,
            paddedBounds.left, paddedBounds.top, 0,
            paddedBounds.left, paddedBounds.bottom, 0,
            paddedBounds.right, paddedBounds.bottom, 0,
        ];
        matrix3D.transformVectors(vectors, vectors);
        var elements = this._elements;
        if (!elements) {
            elements = this._elements = new TriangleElements(new AttributesBuffer(5, 6));
            elements.setPositions(vectors);
            elements.setUVs([0, 0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1]);
        }
        else {
            elements.setPositions(vectors);
        }
        return elements.getAbstraction(this._stage);
    };
    _Render_Renderer.prototype.executeRender = function (enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        if (maskConfig === void 0) { maskConfig = 0; }
        // disable cull, because for render to texture it is bugged
        this._stage.context.setCulling(ContextGLTriangleFace.NONE);
        _super.prototype.executeRender.call(this, enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig);
    };
    _Render_Renderer.prototype._getRenderMaterial = function () {
        return this._asset.getAbstraction(this.renderGroup.getRenderElements(this.stageElements.elements));
    };
    _Render_Renderer.prototype._getStyle = function () {
        return this._asset.style;
    };
    _Render_Renderer.assetType = '[render Renderer]';
    return _Render_Renderer;
}(_Render_RenderableBase));
export { _Render_Renderer };
CacheRenderer.registerMaterial(_Render_RendererMaterial, CacheRenderer);
